import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Alert,
  FormControlLabel,
  Checkbox,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import LoadingButton from '../Loading/LoadingButton';
import PersonComparisonView from './PersonComparisonView';
import FieldMergeStrategy from './FieldMergeStrategy';
import RelationshipPreview from './RelationshipPreview';
import { personsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/useAuth';

export interface PersonMergeDialogProps {
  open: boolean;
  onClose: () => void;
  targetPerson?: any; // Pre-selected target person
  sourcePersons?: any[]; // Pre-selected source persons
  onComplete?: (result: any) => void;
}

interface FieldStrategy {
  name?: 'prefer-target' | 'prefer-longest' | 'prefer-non-null';
  email?: 'prefer-target' | 'prefer-non-null';
  phone?: 'prefer-target' | 'prefer-non-null';
  address?: 'prefer-target' | 'prefer-longest' | 'prefer-non-null';
  nationality?: 'prefer-target' | 'prefer-non-null';
  types?: 'merge-all' | 'prefer-target' | 'prefer-source';
}

const PersonMergeDialog: React.FC<PersonMergeDialogProps> = ({
  open,
  onClose,
  targetPerson,
  sourcePersons = [],
  onComplete,
}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [allPersons, setAllPersons] = useState<any[]>([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [target, setTarget] = useState<any | null>(targetPerson || null);
  const [sources, setSources] = useState<any[]>(sourcePersons);
  const [fieldStrategy, setFieldStrategy] = useState<FieldStrategy>({
    name: 'prefer-target',
    email: 'prefer-target',
    phone: 'prefer-target',
    address: 'prefer-target',
    nationality: 'prefer-target',
    types: 'merge-all',
  });
  const [preview, setPreview] = useState<any | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const [createAliases, setCreateAliases] = useState(true);
  const [flagForReview, setFlagForReview] = useState(true);

  useEffect(() => {
    if (open) {
      // Always load persons when dialog opens
      // The loadPersons function will handle setting target and sources after loading
      loadPersons();
    } else {
      // Reset state when dialog closes
      setTarget(null);
      setSources([]);
      setPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Handle prop changes while dialog is open
  useEffect(() => {
    if (open && allPersons.length > 0) {
      if (targetPerson) {
        const foundTarget = allPersons.find((p: any) => p.id === targetPerson.id);
        if (foundTarget) {
          setTarget(foundTarget);
        } else {
          setTarget(targetPerson);
        }
      }
      if (sourcePersons.length > 0) {
        const foundSources = sourcePersons
          .map((sp: any) => allPersons.find((p: any) => p.id === sp.id) || sp)
          .filter(Boolean);
        if (foundSources.length > 0) {
          setSources(foundSources);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetPerson, sourcePersons, allPersons]);

  const loadPersons = async () => {
    try {
      setLoadingPersons(true);
      const response = await personsAPI.getAll({ page: 1, limit: 5000 });
      const data = response.data?.data || response.data?.items || response.data || [];
      setAllPersons(data);
      
      // If targetPerson was provided but not yet set (e.g., dialog just opened), set it now
      if (targetPerson && !target) {
        const foundTarget = data.find((p: any) => p.id === targetPerson.id);
        if (foundTarget) {
          setTarget(foundTarget);
        } else {
          setTarget(targetPerson);
        }
      }
      
      // If sourcePersons were provided but not yet set, set them now
      if (sourcePersons.length > 0 && sources.length === 0) {
        const foundSources = sourcePersons
          .map((sp: any) => data.find((p: any) => p.id === sp.id) || sp)
          .filter(Boolean);
        if (foundSources.length > 0) {
          setSources(foundSources);
        }
      }
    } catch (error) {
      console.error('Failed to load persons:', error);
      toast.error('Failed to load persons');
    } finally {
      setLoadingPersons(false);
    }
  };

  const loadPreview = async () => {
    if (!target || sources.length === 0) {
      setPreview(null);
      setPreviewError(null);
      return;
    }

    try {
      setLoadingPreview(true);
      setPreviewError(null);
      
      // Remove 'types' from fieldStrategy as backend doesn't support it
      // Types are always merged automatically by the backend
      const { types, ...backendFieldStrategy } = fieldStrategy;
      
      const response = await personsAPI.merge({
        targetPersonId: target.id,
        sourcePersonIds: sources.map(s => s.id),
        fieldStrategy: backendFieldStrategy as Record<string, string>,
        createAliases,
        dryRun: true,
      });
      setPreview(response.data);
      setPreviewError(null);
    } catch (error: any) {
      console.error('Failed to load preview:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load merge preview';
      setPreviewError(errorMessage);
      setPreview(null);
      // Don't show toast for preview errors - just show in UI
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (target && sources.length > 0) {
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, sources, fieldStrategy]);

  const handleMerge = async () => {
    if (!target || sources.length === 0) {
      toast.error('Please select target and at least one source person');
      return;
    }

    if (!hasPermission('persons:manage')) {
      toast.error('You do not have permission to merge persons');
      return;
    }

    if (!window.confirm(`Merge ${sources.length} person(s) into "${target.name}"? This will soft-delete the source persons.`)) {
      return;
    }

    try {
      setMerging(true);
      
      // Remove 'types' from fieldStrategy as backend doesn't support it
      // Types are always merged automatically by the backend
      const { types, ...backendFieldStrategy } = fieldStrategy;
      
      const response = await personsAPI.merge({
        targetPersonId: target.id,
        sourcePersonIds: sources.map(s => s.id),
        fieldStrategy: backendFieldStrategy as Record<string, string>,
        createAliases,
        dryRun: false,
      });
      
      toast.success('Persons merged successfully');
      
      if (onComplete) {
        onComplete(response.data);
      }
      
      // Navigate to merged person detail page
      navigate(`/persons/${target.id}`);
      
      onClose();
    } catch (error: any) {
      console.error('Merge failed:', error);
      toast.error(error.response?.data?.message || 'Failed to merge persons');
    } finally {
      setMerging(false);
    }
  };

  const handleClose = () => {
    setTarget(null);
    setSources([]);
    setPreview(null);
    setPreviewError(null);
    setFieldStrategy({
      name: 'prefer-target',
      email: 'prefer-target',
      phone: 'prefer-target',
      address: 'prefer-target',
      nationality: 'prefer-target',
      types: 'merge-all',
    });
    setCreateAliases(true);
    setFlagForReview(true);
    onClose();
  };

  const availableSources = allPersons.filter(p => !target || p.id !== target.id);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Combine Person Identities</Typography>
          <Chip
            label={`${sources.length} source${sources.length !== 1 ? 's' : ''}`}
            color="primary"
            size="small"
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Person Selection */}
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Select Persons
              </Typography>
              
              <Autocomplete
                options={allPersons}
                getOptionLabel={(option) => option?.name || ''}
                value={target}
                onChange={(_, value) => setTarget(value)}
                loading={loadingPersons}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Person (Keep)"
                    helperText="This person will be kept and updated with merged data"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Typography>{option.name}</Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          ({option.email})
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
              />

              <Autocomplete
                multiple
                options={availableSources}
                getOptionLabel={(option) => option?.name || ''}
                value={sources}
                onChange={(_, value) => setSources(value)}
                loading={loadingPersons}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source Persons (Merge Into Target)"
                    helperText="These persons will be merged into the target and soft-deleted"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Typography>{option.name}</Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          ({option.email})
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Stack>
          </Paper>

          {/* Comparison View */}
          {target && sources.length > 0 && (
            <>
              <PersonComparisonView target={target} sources={sources} />
              
              <FieldMergeStrategy
                target={target}
                sources={sources}
                strategy={fieldStrategy}
                onChange={setFieldStrategy}
              />

              {/* Relationship Preview */}
              {loadingPreview ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : preview ? (
                <RelationshipPreview preview={preview} />
              ) : previewError ? (
                <Alert severity="warning">
                  <Typography variant="subtitle2" gutterBottom>
                    Preview Unavailable
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {previewError}
                  </Typography>
                  <Typography variant="body2">
                    You can still proceed with the merge, but it's recommended to review the preview first.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={loadPreview}
                    sx={{ mt: 1 }}
                  >
                    Retry Preview
                  </Button>
                </Alert>
              ) : (
                <Alert severity="info">
                  <Typography variant="body2">
                    Click "Refresh Preview" to see what will be merged.
                  </Typography>
                </Alert>
              )}

              {/* Options */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Options
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={createAliases}
                        onChange={(e) => setCreateAliases(e.target.checked)}
                      />
                    }
                    label="Create aliases for merged names"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={flagForReview}
                        onChange={(e) => setFlagForReview(e.target.checked)}
                      />
                    }
                    label="Flag merged person for review"
                  />
                </Stack>
              </Paper>

              {/* Warnings */}
              {preview?.warnings && preview.warnings.length > 0 && (
                <Alert severity="warning">
                  <Typography variant="subtitle2" gutterBottom>
                    Warnings
                  </Typography>
                  {preview.warnings.map((warning: string, idx: number) => (
                    <Typography key={idx} variant="body2">
                      • {warning}
                    </Typography>
                  ))}
                </Alert>
              )}
            </>
          )}

          {!target && (
            <Alert severity="info">
              Please select a target person and at least one source person to begin.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={merging}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={loadPreview}
          disabled={!target || sources.length === 0 || loadingPreview || merging}
        >
          Refresh Preview
        </Button>
        <LoadingButton
          loading={merging}
          variant="contained"
          onClick={handleMerge}
          disabled={!target || sources.length === 0 || merging}
        >
          Combine Identities
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PersonMergeDialog;
